import { ConnectionListResult, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionTableDownstreamFlowsFilteredByColumnQuery,
  VirtualConnectionTableDownstreamFlowsFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamFlowsConnection_nodes,
  VirtualConnectionTableDownstreamFlowsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamFlowsFilteredByColumnQuery';
import {
  VirtualConnectionTableDownstreamFlowsQuery,
  VirtualConnectionTableDownstreamFlowsQuery_virtualConnectionTables_downstreamFlowsConnection_nodes,
  VirtualConnectionTableDownstreamFlowsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamFlowsQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  VirtualConnectionTableDownstreamFlowsFilteredByColumnQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamFlowsFilteredByColumnQueryGql';
import {
  VirtualConnectionTableDownstreamFlowsQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamFlowsQueryGql';
import { FlowsDisplayGrid, FlowsDisplayGridRowData, getRowDataForFlow } from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = VirtualConnectionTableDownstreamFlowsQueryVariables;
type QueryResults = VirtualConnectionTableDownstreamFlowsQuery;
type QueryResultListNode = VirtualConnectionTableDownstreamFlowsQuery_virtualConnectionTables_downstreamFlowsConnection_nodes;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableDownstreamFlowsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableDownstreamFlowsFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionTableDownstreamFlowsFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamFlowsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  return virtualConnection ? virtualConnection.downstreamFlowsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.downstreamFlowsConnection : null;
}

export type VirtualConnectionTableDownstreamFlowsQueryGridProps =
  GridSortProps<FlowsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionTableDownstreamFlowsQueryGrid(props: VirtualConnectionTableDownstreamFlowsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredFlowsLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamFlowsFilteredByColumnQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as FlowSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForFlow}
    >
      {({ rowStore }) => <FlowsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamFlowsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as FlowSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForFlow}
    >
      {({ rowStore }) => <FlowsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
